import React, { useRef, useState } from 'react';

// Goals — units and FEE volume, against the calendar.
//
// "Fees", never "Volume". The Pipeline already uses Volume for the sum of sale prices; this is the
// money the office collects (commission + assignment fee). Two screens using one word for two numbers
// forty times apart is how a dashboard loses its reader.
//
// ── Why a pace marker and not a percentage ───────────────────────────────────
// "40% of goal" on the 17th of a 30-day month tells an agent nothing without arithmetic they will not
// do. The notch on the bar is where the calendar says they should be, so ahead/behind is a glance
// rather than a calculation — and the line underneath says what it takes per day from here, which is
// the only number that changes what someone does this afternoon.
//
// Everything here is computed server-side (backend/lib/goalPace.js) and rendered as given. There is
// no second copy of the pace math in the browser to drift from the first.

const fmtMoney = (n) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);
// Units can be fractional — a split closing is half a unit each — but 2.00 should read as 2.
const fmtUnits = (n) => (n == null ? '—' : String(Math.round(n * 100) / 100));

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

// 'YYYY-MM' → 'September'. Split, never `new Date(key)` — that parses as UTC midnight and reads back
// as the previous month for anyone west of Greenwich, which is every user of this app.
function periodName(kind, key) {
  if (kind === 'year') return key;
  const [, m] = String(key).split('-').map(Number);
  return MONTH_NAMES[m - 1] || key;
}

export default function GoalsPanel({ goals, office, onSaved }) {
  const [editing, setEditing] = useState(false);
  if (!goals || !goals.month) return null;
  const promptMe = goals.prompt && !editing;
  // One nudge at a time. The month is the primary ask — it is the one that resets — so when both are
  // owed the year waits until the month is answered, which is the very next render since saving
  // refetches. Two stacked callouts in a column this narrow reads as nagging, and a panel that nags
  // is a panel people stop reading.
  const yearNudge = !promptMe && !editing ? goals.year_nudge : null;

  return (
    <div>
      {promptMe && <Prompt goals={goals} onStart={() => setEditing(true)} onSaved={onSaved} />}
      {yearNudge && <YearNudge goals={goals} kind={yearNudge}
                               onStart={() => setEditing(true)} onSaved={onSaved} />}

      {editing
        ? <GoalForm goals={goals} onClose={() => setEditing(false)} onSaved={onSaved} />
        : (
          <div style={{ padding: '4px 17px 14px' }}>
            {/* The owner can put the KPI strip on Office totals. Goals stay personal either way — there
                is no office goal in this build, and drawing office production against one person's
                target would be the most flattering possible lie. Say which it is. */}
            {office && (
              <p style={{ fontSize: 11.5, color: 'var(--gray-500)', margin: '8px 0 12px' }}>
                Your own production — goals are personal, and don't follow the Office toggle.
              </p>
            )}
            <Block label={`${periodName('month', goals.month.period_key)} · this month`} p={goals.month} />
            <div style={{ height: 1, background: 'var(--navy-border)', margin: '14px 0' }} />
            <Block label={`${goals.year.period_key} · year to date`} p={goals.year} compact />

            <button onClick={() => setEditing(true)} style={linkBtn}>
              {goals.month.units.source === 'none' && goals.month.fees.source === 'none'
                ? 'Set your goals' : 'Edit goals'}
            </button>
          </div>
        )}
    </div>
  );
}

// The one-time ask, shown to an agent who has no goal to measure against at all — their first month,
// in practice. Not a monthly ritual: once a goal exists it carries forward and this stays quiet (see
// routes/dashboard.js loadGoals). The row recording their answer is what stops it coming back if they
// say no (migration 086).
function Prompt({ goals, onStart, onSaved }) {
  const [busy, setBusy] = useState(false);
  const month = periodName('month', goals.month.period_key);

  async function dismiss() {
    setBusy(true);
    try {
      await fetch('/api/goals/dismiss', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_kind: 'month', period_key: goals.month.period_key }),
      });
      onSaved && onSaved();
    } finally { setBusy(false); }
  }

  return (
    <div style={{ background: 'var(--gold-dim)', borderBottom: '1px solid var(--navy-border)', padding: '13px 17px' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>What are you aiming at in {month}?</div>
      <p style={{ fontSize: 12.5, color: 'var(--gray-300)', margin: '0 0 10px', lineHeight: 1.5 }}>
        Set a target for units and fees and the bar below shows whether you're keeping up. It carries
        into next month on its own — you'll only be asked again if you clear it.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onStart} style={primaryBtn}>Set {month} goals</button>
        <button onClick={dismiss} disabled={busy} style={quietBtn}>Not now</button>
      </div>
    </div>
  );
}

// The year's own nudge. Two shapes, because they are two different questions.
//
//   set     there is no target for the year. Asked once; declining is respected for the rest of it.
//   review  there is one, and this quarter has not been looked at. NOT a request to change anything —
//           most quarters the answer is "still right", and that has to be one click or nobody will
//           ever press it.
function YearNudge({ goals, kind, onStart, onSaved }) {
  const [busy, setBusy] = useState(false);
  const y = goals.year;
  const post = async (path) => {
    setBusy(true);
    try {
      await fetch(path, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_kind: 'year', period_key: y.period_key }),
      });
      onSaved && onSaved();
    } finally { setBusy(false); }
  };

  if (kind === 'set') {
    return (
      <div style={nudgeBox}>
        <div style={nudgeTitle}>No target for {y.period_key} yet.</div>
        <p style={nudgeText}>
          A yearly number is what the monthly ones add up to. Set it once and feebe will check in
          each quarter.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onStart} style={primaryBtn}>Set {y.period_key} goal</button>
          <button onClick={() => post('/api/goals/dismiss')} disabled={busy} style={quietBtn}>No thanks</button>
        </div>
      </div>
    );
  }

  // Lead with where they actually are — the check-in is worthless if it asks "still right?" without
  // showing the number it is asking about.
  const fees = y.fees, units = y.units;
  const line = fees.goal != null
    ? `${fmtMoney(fees.actual)} of ${fmtMoney(fees.goal)} — ${Math.round(fees.pct)}% of the year's fees, with ${Math.round(y.elapsed_pct)}% of the year gone.`
    : `${fmtUnits(units.actual)} of ${fmtUnits(units.goal)} units, with ${Math.round(y.elapsed_pct)}% of the year gone.`;

  return (
    <div style={nudgeBox}>
      <div style={nudgeTitle}>{(goals.quarter_key || '').replace('-', ' ')} check-in</div>
      <p style={nudgeText}>{line} Still the right target?</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => post('/api/goals/review')} disabled={busy} style={primaryBtn}>Still right</button>
        <button onClick={onStart} style={quietBtn}>Adjust it</button>
      </div>
    </div>
  );
}

function Block({ label, p, compact }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: 'var(--gray-500)', margin: '10px 0 9px' }}>{label}</div>
      <Bar title="Fees" m={p.fees} fmt={fmtMoney} elapsed={p.elapsed_pct} />
      <div style={{ height: compact ? 9 : 12 }} />
      <Bar title="Units" m={p.units} fmt={fmtUnits} elapsed={p.elapsed_pct} unit="units" />
    </div>
  );
}

function Bar({ title, m, fmt, elapsed, unit }) {
  const unset = m.status === 'unset';
  const tone = m.status === 'hit' ? 'var(--teal)'
    : m.status === 'behind' ? 'var(--gold)' : 'var(--teal)';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{title}</span>
        {m.carried_from && (
          <span style={carriedTag} title={`No goal set for this period — carried forward from ${m.carried_from}`}>
            from {m.carried_from}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
          <strong>{fmt(m.actual)}</strong>
          <span style={{ color: 'var(--gray-500)' }}> / {unset ? 'no goal' : fmt(m.goal)}</span>
        </span>
      </div>

      <div style={{ position: 'relative', height: 9, background: 'var(--navy-light)', borderRadius: 999, overflow: 'visible' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(unset ? 0 : m.pct, 100)}%`, height: '100%', background: tone,
                        borderRadius: 999, transition: 'width 0.4s ease' }} />
        </div>
        {/* Where the calendar says you should be. Drawn ON TOP of the fill and only while a goal
            exists — a pace notch against "no goal" would mark a point on a scale that isn't there. */}
        {!unset && (
          <span aria-hidden style={{
            position: 'absolute', left: `${Math.min(Math.max(m.pace_pct, 0), 100)}%`, top: -3, bottom: -3,
            width: 3, marginLeft: -1.5, borderRadius: 2, background: 'var(--white)',
            boxShadow: '0 0 0 1.5px var(--navy-card)',
          }} />
        )}
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 5, lineHeight: 1.45 }}>
        {unset
          ? `No goal set — ${Math.round(elapsed)}% of the period gone.`
          : <>
              <span style={{ color: m.status === 'behind' ? 'var(--gold)' : 'var(--teal)', fontWeight: 600 }}>
                {m.status === 'hit' ? 'Goal hit'
                  : `${fmt(Math.abs(m.delta))}${unit ? ' ' + unit : ''} ${m.status === 'ahead' ? 'ahead of' : 'behind'} pace`}
              </span>
              {m.status !== 'hit' && m.days_remaining > 0 && (
                <> · {m.days_remaining} {m.days_remaining === 1 ? 'day' : 'days'} left
                  {m.remaining_per_day != null && <> · {fmt(m.remaining_per_day)}{unit ? ' ' + unit : ''}/day to hit it</>}
                </>
              )}
              {m.status !== 'hit' && m.days_remaining === 0 && <> · period over</>}
            </>}
      </div>
    </div>
  );
}

// Empty means "no value here"; anything else is compared as typed, so 45000 and "45000" match.
const asSeed = (v) => (v == null ? '' : String(v));

function GoalForm({ goals, onClose, onSaved }) {
  // Seed from whatever is in force, carried values included: an agent who kept last month's number
  // for three months should not retype it to keep it. Blank when nothing has ever been set.
  const seed = useRef({
    units: asSeed(goals.month.units.goal), fees: asSeed(goals.month.fees.goal),
    yUnits: asSeed(goals.year.units.goal), yFees: asSeed(goals.year.fees.goal),
  }).current;
  const [units, setUnits] = useState(seed.units);
  const [fees, setFees] = useState(seed.fees);
  const [yUnits, setYUnits] = useState(seed.yUnits);
  const [yFees, setYFees] = useState(seed.yFees);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const put = (body) => fetch('/api/goals', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Could not save.'); });

      // ⚠️ Send the fields that CHANGED, and only those. One helper for both rows.
      //
      // The API distinguishes three things: a key absent means "leave it alone", null means "clear
      // it", a number means "set it". Collapsing the first two is what broke this. The old code sent
      // the year only when a box had something in it, so emptying both was a silent no-op against a
      // form that promises "leave a box empty to clear that goal" — raised in review of this PR.
      //
      // The obvious remedy — always send both rows — trades that bug for a worse one: the commonest
      // interaction here is setting a monthly goal and ignoring the year, which would then post an
      // empty year on every save. Comparing against what was loaded says exactly what the agent did.
      const saveRow = async (kind, key, cur, was) => {
        const body = { period_kind: kind, period_key: key };
        if (cur.units !== was.units) body.units_goal = cur.units === '' ? null : cur.units;
        if (cur.fees !== was.fees) body.fees_goal = cur.fees === '' ? null : cur.fees;
        if (body.units_goal === undefined && body.fees_goal === undefined) return;  // untouched
        await put(body);
      };

      // Sequential, not Promise.all — two writes on one session, and the second should not start if
      // the first was refused.
      await saveRow('month', goals.month.period_key,
        { units, fees }, { units: seed.units, fees: seed.fees });
      await saveRow('year', goals.year.period_key,
        { units: yUnits, fees: yFees }, { units: seed.yUnits, fees: seed.yFees });

      onClose();
      onSaved && onSaved();
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  }

  const monthName = periodName('month', goals.month.period_key);

  return (
    <div style={{ padding: '14px 17px 16px' }}>
      <Row label={`${monthName} — fees`} value={fees} onChange={setFees} prefix="$" placeholder="45000" />
      <Row label={`${monthName} — units`} value={units} onChange={setUnits} placeholder="4" />
      <div style={{ height: 1, background: 'var(--navy-border)', margin: '13px 0 11px' }} />
      <Row label={`${goals.year.period_key} — fees`} value={yFees} onChange={setYFees} prefix="$" placeholder="500000" />
      <Row label={`${goals.year.period_key} — units`} value={yUnits} onChange={setYUnits} placeholder="48" />

      <p style={{ fontSize: 11.5, color: 'var(--gray-500)', margin: '12px 0 0', lineHeight: 1.5 }}>
        Change these whenever you like. Leave a box empty to clear that goal.
      </p>
      {err && <p style={{ fontSize: 12, color: 'var(--red)', margin: '8px 0 0' }}>{err}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
        <button onClick={save} disabled={busy} style={primaryBtn}>{busy ? 'Saving…' : 'Save goals'}</button>
        <button onClick={onClose} disabled={busy} style={quietBtn}>Cancel</button>
      </div>
    </div>
  );
}

function Row({ label, value, onChange, prefix, placeholder }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
      <span style={{ fontSize: 12.5, color: 'var(--gray-300)', flex: 1, minWidth: 0 }}>{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {prefix && <span style={{ fontSize: 12.5, color: 'var(--gray-500)' }}>{prefix}</span>}
        <input
          value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          inputMode="decimal"
          style={{ width: 96, font: 'inherit', fontSize: 13, fontVariantNumeric: 'tabular-nums',
                   textAlign: 'right', padding: '5px 8px', borderRadius: 7,
                   border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'inherit' }} />
      </span>
    </label>
  );
}

const nudgeBox = {
  background: 'var(--gold-dim)', borderBottom: '1px solid var(--navy-border)', padding: '13px 17px',
};
const nudgeTitle = { fontSize: 13, fontWeight: 600, marginBottom: 3 };
const nudgeText = { fontSize: 12.5, color: 'var(--gray-300)', margin: '0 0 10px', lineHeight: 1.5 };

const carriedTag = {
  fontSize: 10, fontWeight: 600, letterSpacing: '0.02em', padding: '1px 6px', borderRadius: 999,
  background: 'var(--navy-light)', color: 'var(--gray-500)', whiteSpace: 'nowrap',
};
const primaryBtn = {
  font: 'inherit', fontSize: 12, fontWeight: 600, padding: '6px 13px', borderRadius: 8,
  border: '1px solid var(--teal)', background: 'var(--teal)', color: '#fff', cursor: 'pointer',
};
const quietBtn = {
  font: 'inherit', fontSize: 12, fontWeight: 600, padding: '6px 13px', borderRadius: 8,
  border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-500)', cursor: 'pointer',
};
const linkBtn = {
  font: 'inherit', fontSize: 11.5, fontWeight: 600, color: 'var(--teal)', background: 'none',
  border: 0, padding: 0, marginTop: 14, cursor: 'pointer',
};
